import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { opcoesDeCompetencia } from '@/componentes/competencias';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDataHora, formatarReais } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { api, ROTULO_DO_RATEIO, type CriterioDeRateio, type IndiretoDoPeriodo, type LancamentoIndireto } from './api';
import {
  competenciaEscrita,
  corpoDoIndireto,
  faltaNoEstorno,
  faltaNoIndireto,
  mesQueSeFecha,
  totalVigente,
  type ValoresDoIndireto,
} from './margem';

/**
 * O CUSTO INDIRETO E O RATEIO (0096) na tela de custos: o critério da empresa
 * (parâmetro, como o do combustível), o livro dos lançamentos por centro de
 * custo e competência, e o que o período rateou.
 *
 * O livro é só de inserção (ADR-008): corrigir é estornar, com motivo, e lançar
 * de novo. O estornado continua na lista, com o selo.
 *
 * Sem desenho próprio no manual: segue o cartão do valor-hora (título, texto,
 * ação secundária, tabela) e a janela de até seis campos (p.30) — dúvida de
 * design registrada no relatório.
 */
export function CustoIndireto({
  de,
  ate,
  indireto,
  criterio,
  podeLancar,
  podeEstornar,
  podeConfigurar,
}: {
  readonly de: string;
  readonly ate: string;
  /** O que o período apurou, da mesma resposta da tabela; nulo enquanto carrega. */
  readonly indireto: IndiretoDoPeriodo | null;
  readonly criterio: CriterioDeRateio | null;
  readonly podeLancar: boolean;
  readonly podeEstornar: boolean;
  readonly podeConfigurar: boolean;
}) {
  const base = useId();
  const cliente = useQueryClient();
  const consulta = useQuery({ queryKey: ['custos', 'indireto', de, ate], queryFn: () => api.indireto(de, ate) });
  const lancamentos = useMemo(() => consulta.data?.lancamentos ?? [], [consulta.data]);
  const centros = useMemo(() => (consulta.data?.centros ?? []).filter((c) => c.ativo), [consulta.data]);

  const vazio: ValoresDoIndireto = { centroCustoId: '', mes: mesQueSeFecha(new Date()), valor: '', descricao: '' };
  const [lancando, setLancando] = useState(false);
  const [valores, setValores] = useState<ValoresDoIndireto>(vazio);
  const [tentou, setTentou] = useState(false);
  const falta = faltaNoIndireto(valores);

  const [estornando, setEstornando] = useState<LancamentoIndireto | null>(null);
  const [motivo, setMotivo] = useState('');

  const [trocando, setTrocando] = useState(false);
  const [novoCriterio, setNovoCriterio] = useState<CriterioDeRateio>(criterio ?? 'horas_produtivas');
  const [motivoDoCriterio, setMotivoDoCriterio] = useState('');

  const invalidar = () => void cliente.invalidateQueries({ queryKey: ['custos'] });

  const lancar = useMutation({
    mutationFn: (v: ValoresDoIndireto) => api.lancarIndireto(corpoDoIndireto(v)),
    meta: {
      retorno: retornoDe<{ competencia: string; valor: number }, ValoresDoIndireto>((r, v) => {
        const c = centros.find((x) => x.id === v.centroCustoId);
        return {
          titulo: 'Custo indireto lançado',
          ...(c ? { alvo: { codigo: c.codigo, nome: c.nome } } : {}),
          fatos: [formatarReais(r.valor), `competência ${competenciaEscrita(r.competencia)}`, v.descricao.trim()],
          efeito: { rotulo: 'Rateio', antes: 'fora do custo das máquinas', depois: `entra no custo total de ${competenciaEscrita(r.competencia)}` },
        };
      }),
      silenciarFalha: true,
    },
    onSuccess: () => {
      setLancando(false);
      invalidar();
    },
  });

  const estornar = useMutation({
    mutationFn: (v: { readonly alvo: LancamentoIndireto; readonly motivo: string }) => api.estornarIndireto(v.alvo.id, v.motivo),
    meta: {
      retorno: retornoDe<{ id: string }, { readonly alvo: LancamentoIndireto; readonly motivo: string }>((_r, v) => ({
        titulo: 'Custo indireto estornado',
        alvo: { codigo: v.alvo.centroCodigo, nome: v.alvo.descricao },
        fatos: [formatarReais(v.alvo.valor), `competência ${competenciaEscrita(v.alvo.competencia)}`, `motivo: ${v.motivo}`],
        efeito: { rotulo: 'Rateio do mês', antes: `com ${formatarReais(v.alvo.valor)}`, depois: `sem ${formatarReais(v.alvo.valor)}` },
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      setEstornando(null);
      invalidar();
    },
  });

  const trocar = useMutation({
    mutationFn: (v: { readonly criterio: CriterioDeRateio; readonly motivo: string }) => api.configurarRateio(v.criterio, v.motivo),
    meta: {
      retorno: retornoDe<{ criterioRateioIndireto: CriterioDeRateio; antes: CriterioDeRateio }, { readonly criterio: CriterioDeRateio; readonly motivo: string }>(
        (r, v) => ({
          titulo: 'Critério do rateio trocado',
          alvo: { nome: 'Custos da empresa' },
          fatos: [`motivo: ${v.motivo}`],
          efeito: { rotulo: 'Rateio do custo indireto', antes: ROTULO_DO_RATEIO[r.antes], depois: ROTULO_DO_RATEIO[r.criterioRateioIndireto] },
        }),
      ),
      silenciarFalha: true,
    },
    onSuccess: () => {
      setTrocando(false);
      invalidar();
    },
  });

  const vigente = totalVigente(lancamentos);

  const COLUNAS: readonly Coluna<LancamentoIndireto>[] = [
    {
      chave: 'competencia',
      rotulo: 'Competência',
      celula: (l) => <span className="numero text-tinta">{competenciaEscrita(l.competencia)}</span>,
    },
    {
      chave: 'centro',
      rotulo: 'Centro de custo',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{l.centroCodigo}</span>
          <TextoDaCelula texto={l.descricao} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'registro',
      rotulo: 'Lançado por',
      prioridade: 'secundaria',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{l.registradoPor ?? 'Sistema'}</span>
          <span className="txt-apoio text-tinta-apagada">{formatarDataHora(new Date(l.registradoEm))}</span>
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      celula: (l) =>
        l.estornadoEm ? (
          <span
            title={`${l.estornadoPor ?? 'Alguém'} estornou em ${formatarDataHora(new Date(l.estornadoEm))}: ${l.motivoDoEstorno ?? ''}`}
            className="inline-flex h-[var(--selo-altura)] items-center gap-1.5 rounded-pill bg-nao-alocado-fundo px-2 text-nao-alocado-texto"
          >
            <span aria-hidden="true" className="size-[var(--selo-ponto)] rounded-pill bg-nao-alocado" />
            <span className="txt-selo">Estornado</span>
          </span>
        ) : (
          <span className="txt-corpo text-tinta-secundaria">Entra no rateio</span>
        ),
    },
    {
      chave: 'valor',
      rotulo: 'Valor',
      alinhamento: 'numero',
      celula: (l) => (
        <span className={`numero whitespace-nowrap ${l.estornadoEm ? 'text-tinta-apagada line-through' : 'text-tinta'}`}>{formatarReais(l.valor)}</span>
      ),
      total: () => <span className="numero whitespace-nowrap text-tinta">{formatarReais(vigente)}</span>,
    },
  ];

  const resumo = indireto
    ? [
        `No período: ${formatarReais(indireto.lancado)} lançado`,
        `${formatarReais(indireto.rateado)} rateado às máquinas`,
        indireto.semBase > 0 ? `${formatarReais(indireto.semBase)} sem base para ratear` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  return (
    <section className="cartao flex flex-col gap-4 p-6" aria-labelledby={`${base}-titulo`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-[80ch] flex-col gap-1">
          <h2 id={`${base}-titulo`} className="txt-secao text-tinta">
            Custo indireto e rateio
          </h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Administração, pátio, oficina geral: lançados por centro de custo e mês, e rateados às máquinas pelo critério da
            empresa. Entram no custo total e na margem. Corrigir um lançamento é estornar, com o motivo, e lançar de novo.
          </p>
          {criterio ? (
            <p className="txt-apoio text-pretty text-tinta">{`Critério em uso: ${ROTULO_DO_RATEIO[criterio]}.`}</p>
          ) : (
            <Carregando oQue="Carregando o critério do rateio">
              <Barra className="my-1 h-3 w-64" />
            </Carregando>
          )}
          {resumo ? <p className="txt-apoio text-pretty text-tinta-secundaria">{resumo}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {podeConfigurar ? (
            <Botao
              nivel="fantasma"
              disabled={criterio === null}
              onClick={() => {
                setNovoCriterio(criterio ?? 'horas_produtivas');
                setMotivoDoCriterio('');
                trocar.reset();
                setTrocando(true);
              }}
            >
              Trocar critério
            </Botao>
          ) : null}
          {/* Sem a licença o botão não aparece (prova do marco, 07/10/2026):
              antes ele aparecia desligado, com a licença na dica. */}
          {podeLancar ? (
            <Botao
              nivel="secundario"
              disabled={centros.length === 0}
              title={centros.length === 0 ? 'Cadastre um centro de custo em Cadastros primeiro.' : undefined}
              onClick={() => {
                setValores(vazio);
                setTentou(false);
                lancar.reset();
                setLancando(true);
              }}
            >
              Lançar custo indireto
            </Botao>
          ) : null}
        </div>
      </div>

      <Tabela
        rotulo="Lançamentos de custo indireto"
        colunas={COLUNAS}
        linhas={lancamentos}
        chaveDaLinha={(l) => l.id}
        carregando={consulta.isPending}
        alturaDupla
        rotuloDoTotal="Vale no rateio"
        {...(podeEstornar
          ? {
              acoes: (l: LancamentoIndireto) => (
                <MaisAcoes
                  acoes={[
                    {
                      rotulo: 'Estornar lançamento',
                      aoEscolher: () => {
                        setMotivo('');
                        estornar.reset();
                        setEstornando(l);
                      },
                      ...(l.estornadoEm ? { motivoIndisponivel: 'Este lançamento já foi estornado.' } : {}),
                    },
                  ]}
                />
              ),
            }
          : {})}
        erro={consulta.isError ? <ErroDaLista oQue="Os lançamentos de custo indireto" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={
          <EstadoVazio
            titulo="Nenhum custo indireto lançado no período"
            descricao={
              (consulta.data?.centros ?? []).length === 0
                ? 'O custo indireto é lançado por centro de custo. Cadastre os centros em Cadastros e volte para lançar o do mês.'
                : 'Sem lançamento, o custo total do mês fica parcial: lance a administração, o pátio e a oficina geral do mês.'
            }
            acao={
              (consulta.data?.centros ?? []).length === 0 ? (
                <Link to="/cadastros" className="botao botao-secundario">
                  Abrir cadastros
                </Link>
              ) : undefined
            }
          />
        }
      />

      {/* ------------------------------------------------ lançar */}
      <Janela
        titulo="Lançar custo indireto"
        descricao="Entra no custo total da competência, rateado às máquinas pelo critério da empresa."
        aberta={lancando && podeLancar}
        aoFechar={() => {
          setLancando(false);
        }}
        acoes={
          <>
            <Botao
              nivel="terciario"
              onClick={() => {
                setLancando(false);
              }}
            >
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={lancar.isPending}
              onClick={() => {
                setTentou(true);
                if (Object.keys(falta).length > 0) return;
                lancar.mutate(valores);
              }}
            >
              {lancar.isPending ? 'Lançando…' : 'Lançar custo'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {lancar.isError ? (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
              <span className="txt-corpo text-tinta">{explicarFalha(lancar.error).erro}</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(lancar.error).comoResolver}</span>
            </div>
          ) : null}
          <CampoSelecao
            rotulo="Centro de custo"
            valor={valores.centroCustoId}
            aoMudar={(centroCustoId) => {
              setValores({ ...valores, centroCustoId });
            }}
            opcoes={centros.map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome}` }))}
            rotuloVazio="Escolha o centro de custo"
          />
          {tentou && falta.centroCustoId ? <p className="txt-ajuda text-erro">{falta.centroCustoId}</p> : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Por nome ("Setembro de 2026"), e não `type="month"`: sem seletor de mês no navegador, ele vira caixa de texto aaaa-mm (achado dos robôs, 20/10/2026). */}
            <CampoSelecao
              rotulo="Competência"
              valor={valores.mes ? `${valores.mes}-01` : ''}
              aoMudar={(v) => {
                setValores({ ...valores, mes: v.slice(0, 7) });
              }}
              opcoes={opcoesDeCompetencia(new Date(), valores.mes ? `${valores.mes}-01` : '')}
              rotuloVazio="Escolha o mês"
              erro={tentou ? (falta.mes ?? null) : null}
              ajuda="O mês em que o custo é reconhecido."
            />
            <CampoTexto
              rotulo="Valor (R$)"
              valor={valores.valor}
              aoMudar={(valor) => {
                setValores({ ...valores, valor });
              }}
              placeholder="18.500,00"
              validar={(texto) => faltaNoIndireto({ ...valores, valor: texto }).valor ?? null}
            />
          </div>
          <CampoTexto
            rotulo="Do que é o custo"
            valor={valores.descricao}
            aoMudar={(descricao) => {
              setValores({ ...valores, descricao });
            }}
            maxLength={200}
            placeholder="Aluguel do pátio de setembro"
            ajuda="Fica no lançamento e na trilha."
            validar={(texto) => faltaNoIndireto({ ...valores, descricao: texto }).descricao ?? null}
          />
        </div>
      </Janela>

      {/* ------------------------------------------------ estornar (ADR-008) */}
      <Janela
        titulo="Estornar lançamento"
        aberta={estornando !== null && podeEstornar}
        aoFechar={() => {
          setEstornando(null);
        }}
        acoes={
          <>
            <Botao
              nivel="terciario"
              onClick={() => {
                setEstornando(null);
              }}
            >
              Manter o lançamento
            </Botao>
            <Botao
              nivel="destrutivo"
              disabled={faltaNoEstorno(motivo) !== null || estornar.isPending}
              title={faltaNoEstorno(motivo) ?? undefined}
              onClick={() => {
                if (estornando) estornar.mutate({ alvo: estornando, motivo: motivo.trim() });
              }}
            >
              {estornar.isPending ? 'Estornando…' : 'Estornar lançamento'}
            </Botao>
          </>
        }
      >
        {estornando === null ? null : (
          <div className="flex flex-col gap-4">
            {estornar.isError ? (
              <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
                <span className="txt-corpo text-tinta">{explicarFalha(estornar.error).erro}</span>
                <span className="txt-apoio text-tinta-secundaria">{explicarFalha(estornar.error).comoResolver}</span>
              </div>
            ) : null}
            <p className="txt-corpo text-pretty text-tinta">
              {`Estornar ${formatarReais(estornando.valor)} de ${estornando.centroCodigo} em ${competenciaEscrita(estornando.competencia)}? O valor sai do rateio do mês, e o lançamento continua na lista, marcado como estornado. Para o valor certo, lance de novo.`}
            </p>
            <CampoTexto
              rotulo="Por que este lançamento está errado"
              valor={motivo}
              aoMudar={setMotivo}
              maxLength={300}
              placeholder="Valor digitado com um zero a mais"
              ajuda="Escreva o que houve, não só que houve. Pelo menos 15 caracteres."
              validar={faltaNoEstorno}
            />
          </div>
        )}
      </Janela>

      {/* ------------------------------------------------ o critério (custo.configurar) */}
      <Janela
        titulo="Trocar critério do rateio"
        {...(criterio ? { descricao: `Em uso: ${ROTULO_DO_RATEIO[criterio]}.` } : {})}
        aberta={trocando}
        aoFechar={() => {
          setTrocando(false);
        }}
        acoes={
          <>
            <Botao
              nivel="terciario"
              onClick={() => {
                setTrocando(false);
              }}
            >
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={trocar.isPending || novoCriterio === criterio || faltaNoMotivo(motivoDoCriterio) !== null}
              onClick={() => {
                trocar.mutate({ criterio: novoCriterio, motivo: motivoDoCriterio.trim() });
              }}
            >
              {trocar.isPending ? 'Trocando…' : 'Trocar critério'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {trocar.isError ? (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
              <span className="txt-corpo text-tinta">{explicarFalha(trocar.error).erro}</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(trocar.error).comoResolver}</span>
            </div>
          ) : null}
          <CampoSelecao
            rotulo="Ratear o custo indireto por"
            valor={novoCriterio}
            aoMudar={(v) => {
              setNovoCriterio(v as CriterioDeRateio);
            }}
            opcoes={(Object.keys(ROTULO_DO_RATEIO) as CriterioDeRateio[]).map((c) => ({ valor: c, rotulo: ROTULO_DO_RATEIO[c] }))}
          />
          <CampoDoMotivo
            rotulo="Motivo"
            valor={motivoDoCriterio}
            aoMudar={setMotivoDoCriterio}
            exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['rateio.trocar']}`}
          />
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            O critério muda o custo total e a margem de todo período consultado, inclusive meses já vistos.
          </p>
        </div>
      </Janela>
    </section>
  );
}
